<template>
  <div id="eventSchedule" style="position: relative">
    <template v-if="$apollo.queries.event.loading || !event.id">
      <v-container>
        <AppLoadingPage />
      </v-container>
    </template>
    <template v-else>
      <v-container
        class="container-content py-0"
        style="padding-top: 0px !important"
      >
        <div class="mt-8">
          <v-tabs
            v-model="tabSelected"
            hide-slider
            :ripple="false"
            class="fipa-tabs"
          >
            <v-tab
              class="bolded-text"
              data-test="main"
              href="#classes"
              value="#classes"
            >
              {{
                $t(
                  "events:EventPage.Schedule.ClassesAndFees.tabheading",
                  "Classes & Fees",
                  "Schedule section tab heading on event page for classes and fees"
                )
              }}
            </v-tab>
            <v-tab
              class="bolded-text"
              data-test="provisions"
              href="#provisions"
              value="#provisions"
            >
              {{
                $t(
                  "events:EventPage.Schedule.SpecialProvisions.tabheading",
                  "Special Provisions",
                  "Schedule section tab heading on event page for special provisions"
                )
              }}
            </v-tab>
            <v-tab
              class="bolded-text"
              data-test="documents"
              href="#documents"
              value="#documents"
            >
              {{
                $t(
                  "events:EventPage.Schedule.Documents.tabheading",
                  "Documents",
                  "Schedule section tab heading on event page for documents"
                )
              }}
            </v-tab>
            <v-tab
              class="bolded-text"
              data-test="eventStaff"
              href="#details"
              value="#details"
            >
              {{
                $t(
                  "events:EventPage.Schedule.OrganisersAndOfficials.tabheading",
                  "Organisers & Officials",
                  "Schedule section tab heading on event page for Organisers & Officials"
                )
              }}
            </v-tab>
            <v-tab
              class="bolded-text"
              data-test="participants"
              href="#participants"
              value="#participants"
            >
              {{
                $t(
                  "events:EventPage.Schedule.Participants.tabheading",
                  "Participants",
                  "Schedule section tab heading on event page for Participants"
                )
              }}
            </v-tab>
          </v-tabs>
        </div>
      </v-container>
      <div :class="notFinal" />
      <v-container
        :class="notFinal.length > 0 ? 'not-final-contents' : ''"
        class="remove-mobile-container-padding"
        style="padding-top: 12px !important; position: inherit"
      >
        <template v-if="notFinal.length > 0">
          <div
            class="d-flex flex-row mb-3 mt-6"
            style="align-items: center; justify-content: center"
          >
            <v-icon color="red"> $iconBell </v-icon>
            <div class="mx-2" style="z-index: 1">
              <b style="font-size: 15px">
                {{
                  $t(
                    "events:EventPage.Schedule.NotApproved.message",
                    "The schedule is not yet approved"
                  )
                }}
              </b>
            </div>
            <div style="z-index: 1">
              <AppShowEventStatus
                :event="{ status: 'draft' }"
                no-button
                always-show
                variant="outlined"
              />
            </div>
          </div>
        </template>
        <AppTabItems ref="validTabs" v-model="tabSelected" class="my-4">
          <AppTabItem value="#classes">
            <EventCompetitionsManagement
              :event="event"
              @refresh="$apollo.queries.event.refresh()"
            />

            <div
              v-if="
                (event.scheduleVisible &&
                  event.eventProducts &&
                  event.eventProducts.length > 0) ||
                $allowedMutation(
                  event.allowedMutations,
                  'eventEventProductCreateOrUpdate'
                )
              "
              class="my-6"
              style="position: relative"
            >
              <EventProducts
                :event="event"
                @refetch="$apollo.queries.event.refetch()"
              />
            </div>
          </AppTabItem>
          <AppTabItem value="#provisions">
            <EventInfoFields :event="event" />
          </AppTabItem>

          <!-- Documents -->
          <AppTabItem value="#documents">
            <EventDocuments :event="event" />
          </AppTabItem>

          <AppTabItem value="#details">
            <EventContact :event="event" />
          </AppTabItem>
          <AppTabItem value="#participants">
            <EventParticipants
              ref="participants"
              :event-id="eventId"
              :discipline-name="event.discipline.name"
              @vue:mounted="openClassParticipantListWhenMounted"
            />
          </AppTabItem>
        </AppTabItems>

        <div
          v-if="event.status == 'registration_open'"
          class="d-flex text-center justify-center"
          style="width: 100%"
        >
          <AppShowRegistrationBtn
            v-if="$vuetify.display.mdAndUp"
            :event="event"
          />
          <AppShowRegistrationBtn
            v-else
            max-width="295px"
            min-height="40px!important"
            width="100%"
            :event="event"
          />
        </div>
      </v-container>
    </template>
  </div>
</template>
<script>
import { ref, watch, nextTick, computed } from "vue";
import gql from "fraql";

import { EVENT_COMPETITIONS_FRAGMENTS } from "../competition/EventCompetitions/index.vue";

import EventParticipants from "./EventParticipants/index.vue";
import EventCompetitionsManagement from "./EventCompetitionsManagement.vue";
import EventRolesManagement from "./EventRolesManagement.vue";
import EventInfoFields from "./EventInfoFields.vue";
import EventDocuments from "./EventDocuments/index.vue";
import EventContact from "./EventContact.vue";

import EventProducts from "@/components/event/EventProducts/index.vue";
import AppShowEventStatus from "@/components/elements/AppShows/event/AppShowEventStatus.vue";
import AppShowRegistrationBtn from "@/components/elements/AppShows/event/AppShowRegistrationBtn.vue";

import AppLoadingPage from "@/components/elements/AppLoadingPage.vue";

import { useRouteScroll } from "@composables/route_scroll.ts";

import AppTabItems from "@elements/tab/AppTabItems.vue";
import AppTabItem from "@elements/tab/AppTabItem.vue";
import { useTabNavigationHelper } from "@composables/tabNavigationHelper";

export default {
  name: "EventSchedule",
  components: {
    EventInfoFields,
    EventDocuments,
    EventContact,
    AppLoadingPage,
    AppShowRegistrationBtn,
    AppShowEventStatus,
    EventProducts,
    EventCompetitionsManagement,
    EventParticipants,

    AppTabItems,
    AppTabItem
  },
  provide() {
    return {
      openClassParticipantList: this.openClassParticipantList
    };
  },
  props: {
    eventId: {
      type: String,
      default: null
    }
  },
  setup() {
    const { scrollIntoElement, routeScrollParam } = useRouteScroll();

    const openClassParticipantListWhenMounted = ref(() => null);
    const limitEntriesDialog = ref(false);
    const limitEntriesDefaultDate = ref(null);
    const showAlert = ref(true);

    const classesPrevScroll = ref({ x: 0, y: 0 });
    const event = ref({
      name: "",
      id: null,
      organiser: {
        name: ""
      },
      competitions: [],
      allowedMutations: []
    });

    const { tabSelected, manualNavigationHash } =
      useTabNavigationHelper("#classes");

    watch(tabSelected, async (n, o) => {
      if (
        n === "#classes" &&
        o === "#participants" &&
        classesPrevScroll.value.y
      ) {
        await nextTick();
        window.requestAnimationFrame(() => {
          window.scrollTo(0, classesPrevScroll.value.y);
          classesPrevScroll.value = { x: 0, y: 0 };
        });
      }
    });

    const notFinal = computed(() => {
      if (
        [
          "draft",
          "proposed",
          "published",
          "schedule_proposed",
          "declined",
          "schedule_declined"
        ].includes(event.value.status) &&
        tabSelected.value == "#classes"
      )
        return "not-final";

      return "";
    });

    return {
      tabSelected,
      classesPrevScroll,
      event,
      notFinal,

      scrollIntoElement,
      routeScrollParam,

      limitEntriesDialog,
      limitEntriesDefaultDate,
      showAlert,

      openClassParticipantListWhenMounted,
      manualNavigationHash
    };
  },
  apollo: {
    event: {
      query: gql`
        query eventSchedule($id: ID) {
          event(id: $id) {
            id
            name
            status
            description
            startDate
            endDate
            registrationEndDate
            alertText

            ${AppShowRegistrationBtn.fragments.eventWithMutations}
            ${EventCompetitionsManagement.fragments.eventLimitEventFields}

            eventLimits {
              ${EventCompetitionsManagement.fragments.eventLimitAlertFields}
              ${EventCompetitionsManagement.fragments.eventLimitFields}
              ${EVENT_COMPETITIONS_FRAGMENTS.competitionDayLimitsFormFields}
            }
           
            scheduleVisible

            contactPersonName
            contactEmail
            contactPhone
            otherContactInfo
            
            useOnlinePayments
            useOnlinePaymentsForPrizeMoney
        
            ${EventRolesManagement.fragments.eventFields}
            eventStaffFunctionsConf {
              visibility
              allowedMutations
            }
            disciplineId 
            discipline @client {
              id
              name
              ${EventContact.fragments.disciplineFields}
            }
            organiser {
              id
              name
              fullName
              logoPath
              addressCountryId
              addressCity
              addressIndex
              addressText
              phone
              email
              hasVatObligation
              regionId
              region @client {
                id
                name
              }
            }
        
            ${EventRolesManagement.fragments.personRoles}
        
            competitions {
              ${EVENT_COMPETITIONS_FRAGMENTS.competitionFields}
              ${EventCompetitionsManagement.fragments.tagExplanationFields}
              ${EventCompetitionsManagement.fragments.competitionFields}
            }
            
            ${EventInfoFields.fragments.infoFields}
            place
            startingListUrl
            broadcastUrl
            homepageUrl
            ${EventProducts.fragments.eventProducts}
            allowedFields
            allowedMutations
            useEquipe
          }
        }
      `,
      variables() {
        return {
          id: this.eventId
        };
      },
      manual: true,
      result({ loading, data }) {
        if (loading || !data || !data.event) return;
        this.event = data.event;
        if (this.$route.hash != "#participants") {
          // participants tab has own this.scrollIntoElement() to call after lazy loading
          this.scrollIntoElement();
        }
      },
      fetchPolicy: "cache-and-network"
    }
  },
  methods: {
    /**
     * Clicking box in "Confirmed entries" column should move user to participants tab and show selected class' participants
     * This method is provided to EventCompetitions component
     * @param {string} classId
     */
    async openClassParticipantList(classId) {
      if (!classId) return;
      this.classesPrevScroll = { x: 0, y: window.scrollY };
      this.manualNavigationHash("#participants");
      await this.$nextTick();

      // needs to be here so it can properly call method
      if (!this.$refs.participants) {
        this.openClassParticipantListWhenMounted = () => {
          this.$refs.participants.openClassParticipantsList(classId);
        };
        return;
      }
      this.$refs.participants.openClassParticipantsList(classId);
    }
  }
};
</script>
<style lang="scss">
.schedule-organiser-logo img {
  max-width: 150px !important;
  max-height: 150px !important;
  width: auto !important;
}
.not-final {
  margin-top: 0px;
  position: relative;
  z-index: 0;
  position: absolute;
  top: 60px;
  bottom: -100px;
  box-shadow: 0 0 2px 0 #d0021b;
  left: 0;
  right: 0;
  background-color: #ebebeb;
  border-radius: 2px;
  @media (max-width: 959px) {
    top: 90px;
  }
}
.not-final-contents {
  table,
  .v-window {
    margin-top: 0 !important;
    background-color: #ebebeb !important;
  }
}
</style>
